import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Smartphone, Banknote, CreditCard, X, Loader, MapPin, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_ENDPOINTS, fetchWithTimeout } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { AddressModal } from '../components/AddressModal';

interface APIAddress {
  address_id: string;
  label: string;
  place: string;
  latitude: string;
  longitude: string;
}

interface Address {
  id: string;
  label: string;
  address: string;
  lat: number;
  lng: number;
}

interface MpesaModalProps {
  onClose: () => void;
  onSubmit: (phoneNumber: string) => void;
  total: number;
}

const MpesaModal: React.FC<MpesaModalProps> = ({ onClose, onSubmit, total }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber) {
      setError('Phone number is required');
      return;
    }
    if (!/^(?:\+254|0)[17]\d{8}$/.test(phoneNumber)) {
      setError('Please enter a valid Kenyan phone number');
      return;
    }
    onSubmit(phoneNumber);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">M-Pesa Payment</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Enter the M-Pesa phone number to pay KSH {total}
            </p>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                setError('');
              }}
              placeholder="e.g., 0712345678"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400"
            />
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Proceed to Pay
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DELIVERY_FEE = 1;

export const Payment: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useCart();
  const { user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState<'mpesa' | 'cash'>('mpesa');
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const formData = new URLSearchParams();
        formData.append('user_id', user?.user_id || '');
  
        const response = await fetchWithTimeout(API_ENDPOINTS.GET_ADDRESSES, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          mode: 'cors',
          body: formData,
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data: { success: boolean; data: APIAddress[] } = await response.json();
  
        if (data.success) {
          const formattedAddresses: Address[] = data.data.map((addr: APIAddress) => ({
            id: addr.address_id,
            label: addr.label,
            address: addr.place,
            lat: parseFloat(addr.latitude),
            lng: parseFloat(addr.longitude),
          }));
          setAddresses(formattedAddresses);
          if (formattedAddresses.length > 0) {
            setSelectedAddressId(formattedAddresses[0].id);
          }
        } else {
          setError('Failed to fetch addresses');
        }
      } catch (err) {
        console.error('Error fetching addresses:', err);
        setError('Failed to load addresses. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
  
    if (state.items.length === 0) {
      navigate('/');
      return;
    }
  
    if (!user?.user_id) {
      navigate('/checkout');
      return;
    }
  
    fetchAddresses();
  }, [state.items.length, navigate, user?.user_id]);
  

  const handleAddressUpdate = (address: Address) => {
    setAddresses([...addresses, address]);
    setSelectedAddressId(address.id);
    setShowAddressModal(false);
  };

  const createOrder = async (paymentDetails: { method: 'mpesa' | 'cash'; phone?: string }) => {
    if (!user?.user_id) {
      setError('Please log in to continue');
      return;
    }

    if (!selectedAddressId) {
      setError('Please select a delivery address');
      return;
    }

    setIsCreatingOrder(true);
    setError(null);

    try {
      // Prepare order items
      const orderItems = state.items.map(item => {
        const [productId, variationId] = item.productId.split('-');
        const subTotal = item.price * item.quantity;
        
        return {
          product_id: productId,
          variation_id: variationId,
          quantity: item.quantity.toString(),
          size: item.size || '30',
          sub_total: subTotal.toString(),
          type: item.type === 'new' ? 'New Purchase' : 'Refill'
        };
      });

      const orderData = {
        user_id: user.user_id,
        total_amount: (state.total + DELIVERY_FEE).toString(),
        delivery_fee: DELIVERY_FEE.toString(),
        delivery_address: selectedAddressId,
        status: 'placed',
        order_items: orderItems,
        payment: {
          method: paymentDetails.method,
          status: 'pending',
          phone_number: paymentDetails.phone || '',
          ref_id: '****',
          date: null,
          amount: (state.total + DELIVERY_FEE).toString()
        }
      };

      const response = await fetchWithTimeout(API_ENDPOINTS.CREATE_ORDER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(orderData)
      });

      const data = await response.json();
      
      if (data.success) {
        dispatch({ type: 'CLEAR_CART' });
        navigate('/orders?type=placed');
      } else {
        setError(data.message || 'Failed to create order');
      }
    } catch (err) {
      console.error('Error creating order:', err);
      setError('Failed to create order. Please try again later.');
    } finally {
      setIsCreatingOrder(false);
      setShowMpesaModal(false);
    }
  };

  const handleMpesaSubmit = (phoneNumber: string) => {
    createOrder({ method: 'mpesa', phone: phoneNumber });
  };

  const handlePayment = () => {
    if (!selectedAddressId) {
      setError('Please select a delivery address');
      return;
    }

    if (paymentMethod === 'mpesa') {
      setShowMpesaModal(true);
    } else {
      createOrder({ method: 'cash' });
    }
  };

  const subtotal = state.total;
  const total = subtotal + DELIVERY_FEE;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back</span>
      </button>

      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Payment</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/50 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Delivery Address */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Delivery Address</h2>
              </div>
              <button
                onClick={() => setShowAddressModal(true)}
                className="flex items-center space-x-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
              >
                <Plus className="h-4 w-4" />
                <span>Add New Address</span>
              </button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-8 w-8 animate-spin text-green-600" />
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 dark:text-gray-400 mb-4">No saved addresses found</p>
                <button
                  onClick={() => setShowAddressModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add New Address
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {addresses.map((address) => (
                  <label
                    key={address.id}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedAddressId === address.id
                        ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="address"
                      value={address.id}
                      checked={selectedAddressId === address.id}
                      onChange={(e) => setSelectedAddressId(e.target.value)}
                      className="mt-1"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900 dark:text-white">{address.label}</p>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{address.address}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Payment Options */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Select Payment Method
            </h2>

            <div className="space-y-4">
              <button
                onClick={() => setPaymentMethod('mpesa')}
                className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                  paymentMethod === 'mpesa'
                    ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Smartphone className={`h-6 w-6 ${
                    paymentMethod === 'mpesa' ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">M-Pesa</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pay via M-Pesa mobile money</p>
                  </div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 ${
                  paymentMethod === 'mpesa'
                    ? 'border-green-600 bg-green-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {paymentMethod === 'mpesa' && (
                    <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 transform scale-50" />
                  )}
                </div>
              </button>

              <button
                onClick={() => setPaymentMethod('cash')}
                className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                  paymentMethod === 'cash'
                    ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Banknote className={`h-6 w-6 ${
                    paymentMethod === 'cash' ? 'text-green-600' : 'text-gray-400'
                  }`} />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">Cash on Delivery</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pay when your order arrives</p>
                  </div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 ${
                  paymentMethod === 'cash'
                    ? 'border-green-600 bg-green-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {paymentMethod === 'cash' && (
                    <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 transform scale-50" />
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 sticky top-24">
            <div className="flex items-center space-x-2 mb-4">
              <CreditCard className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Order Summary</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between text-gray-600 dark:text-gray-300">
                <span>Subtotal</span>
                <span>KSH {subtotal}</span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-300">
                <span>Delivery Fee</span>
                <span>KSH {DELIVERY_FEE}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex justify-between text-lg font-semibold text-gray-900 dark:text-white">
                  <span>Total</span>
                  <span>KSH {total}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handlePayment}
              disabled={isCreatingOrder || !selectedAddressId}
              className="w-full mt-6 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isCreatingOrder ? (
                <>
                  <Loader className="animate-spin h-5 w-5" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>
                  {paymentMethod === 'mpesa' ? 'Pay with M-Pesa' : 'Place Order'}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Address Modal */}
      {showAddressModal && (
        <AddressModal
          onSave={handleAddressUpdate}
          onClose={() => setShowAddressModal(false)}
        />
      )}

      {/* M-Pesa Modal */}
      {showMpesaModal && (
        <MpesaModal
          onClose={() => setShowMpesaModal(false)}
          onSubmit={handleMpesaSubmit}
          total={total}
        />
      )}
    </div>
  );
};